import { PureComponent } from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import classNames from 'classnames'
import Button from 'beam/dist/Button'
import BeamIcon from 'beam/dist/BeamIcon'
import TextField from 'beam/dist/TextField'
import Tooltip from 'beam/dist/Tooltip'
import withStyles, { Theme, WithStyles } from 'beam/dist/styles'
import { ConditionalWrap } from 'beam/dist/utils'
import Comment from './Comment'
import { userCanComment } from './helpers'
import { AuthenticatedUserResult, withAuthenticatedUser } from '../../../Permissions'
import { Paper } from 'beam/dist/Paper'
import { InputLabel } from 'beam/dist/Input'
import { EvalComment } from './evaluationForm.api'
import { SelectedChatBubbleContext, IndexedChatBubble } from '../ChatMapPanel/constants'
import { formatDuration } from '../../../Common/Utils'

const styles = (theme: Theme) => ({
  root: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(3),
    '@media print': {
      display: 'none',
    },
  },
  headerButton: {
    display: 'flex',
    alignItems: 'center',
    border: 'none',
    padding: 0,
    background: 'none',
    outline: 'none',
    cursor: 'pointer',
    ...theme.typography.caption,
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
  headerButtonDisabled: {
    color: theme.palette.action.disabled,
    cursor: 'not-allowed',
  },
  headerButtonIcon: {
    marginLeft: -theme.spacing(1),
  },
  pipeDivider: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  comments: {
    background: 'rgba(97, 181, 245, 0.08)',
    overflowWrap: 'break-word',
    display: 'none',
    '@media print': {
      display: 'block',
    },
  },
  showComments: {
    display: 'block',
  },
  commentForm: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(-3),
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    padding: theme.spacing(2),
    paddingTop: 0,
    background: 'rgba(97, 181, 245, 0.08)',
    '@media print': {
      display: 'none',
    },
  },
  textFieldRoot: {
    background: theme.palette.background.default,
  },
  timeOffset: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(1),
  },
  timeOffsetFields: {
    display: 'flex',
    paddingTop: theme.spacing(1),
  },
  timeOffsetField: {
    flex: `0 0 ${theme.spacing(11)}px`,
    marginRight: theme.spacing(1),
  },
  timeOffsetNumberInput: {
    height: 'initial',
    padding: '.5em .5em',
    background: theme.palette.background.default,
  },
  commentFormFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(1),
    '& > *': {
      marginLeft: theme.spacing(1),
    },
  },
  isHidden: {
    opacity: 0,
    visibility: 'hidden',
  },
} as const)

interface DraftComment {
  text: string,
  contactOffset: number,
  sectionFK: number | null,
  questionFK: number | null,
}

export interface EvaluationCommentsProps {
  comments: EvalComment[],
  sectionId?: number | null,
  questionId?: number | null,
  testIdPrefix: string,
  leaveCommentMessage?: string | null,
  callDuration: number,
  disabled: boolean,
  onGetCallTime: () => $TSFIXME,
  onCommentUpdate: (comment: DraftComment, ref: string) => void,
  onCommentAdd: (comment: DraftComment) => void,
  authenticatedUser: AuthenticatedUserResult,
  timezone: string,
  indexedChatBubbles: IndexedChatBubble[],
}

interface EvaluationCommentsState {
  showComments: boolean,
  showForm: boolean,
  editComment: boolean,
  editingComment: EvalComment | null,
  editCommentRef: $TSFIXME,
  commentText: string,
  commentValid: boolean,
  submittingComment: boolean,
  contactMinutesOffset: number,
  contactSecondsOffset: number,
  maxMinutesOffset: number,
  maxSecondsOffset: number,
}

class EvaluationComments extends PureComponent<EvaluationCommentsProps & WithStyles<typeof styles> & WithTranslation, EvaluationCommentsState> {
  public static defaultProps: Partial<EvaluationCommentsProps> = {
    sectionId: null,
    questionId: null,
    leaveCommentMessage: null,
    // @ts-expect-error TODO: fix this error
    timezone: false,
  }

  public state: EvaluationCommentsState = {
    showComments: false,
    showForm: false,
    editComment: false,
    editingComment: null,
    editCommentRef: null,
    commentText: '',
    commentValid: true,
    submittingComment: false,
    contactMinutesOffset: 0,
    contactSecondsOffset: 0,
    maxMinutesOffset: 0,
    maxSecondsOffset: 0,
  }

  public componentDidMount() {
    const { callDuration } = this.props
    const [minutes, seconds] = this.convertMs(callDuration)

    this.setState({
      maxMinutesOffset: minutes,
      maxSecondsOffset: seconds,
    })
  }

  public componentDidUpdate(prevProps: EvaluationCommentsProps) {
    const { comments } = this.props
    if (prevProps.comments.length !== comments.length || !prevProps.comments.every(e => comments.includes(e))) {
      this.resetFormState()
    }
  }

  public convertMs(millis: number): [number, number] {
    let seconds = Math.floor(millis / 1000)
    let minutes = Math.floor(seconds / 60)
    seconds = seconds % 60
    minutes = minutes % 60

    return [minutes, seconds]
  }

  public resetFormState = () => {
    this.setState({
      showForm: false,
      editComment: false,
      editingComment: null,
      editCommentRef: null,
      commentText: '',
      commentValid: true,
      submittingComment: false,
      // @ts-expect-error TODO: remove? appears unused
      contactOffset: 0,
    })
  }

  public toggleShowComments = () => {
    this.setState(prevState => ({ showComments: !prevState.showComments }))
  }

  public handleAddCommentClick = () => {
    const { disabled } = this.props

    if (!disabled) {
      this.showForm()
    }
  }

  public showForm = () => {
    const { onGetCallTime } = this.props
    const { editComment, contactMinutesOffset, contactSecondsOffset } = this.state
    const [minutesOffset, secondsOffset] = editComment
      ? [contactMinutesOffset, contactSecondsOffset]
      : this.convertMs(onGetCallTime())

    this.setState({
      showForm: true,
      contactMinutesOffset: minutesOffset,
      contactSecondsOffset: secondsOffset,
    })
  }

  public hideForm = () => {
    this.setState({
      editComment: false,
      editingComment: null,
      editCommentRef: null,
      showForm: false,
      commentText: '',
    })
  }

  public handleCommentEdit = (comment: EvalComment) => {
    const [minutes, seconds] = this.convertMs(comment.contactOffset)

    this.setState({
      showForm: true,
      editComment: true,
      editingComment: comment,
      editCommentRef: comment['$ref'],
      commentText: comment.text,
      commentValid: comment.text.length <= 3000,
      contactMinutesOffset: minutes,
      contactSecondsOffset: seconds,
    })
  }

  public handleCommentChange = (event: { target: { value: string } }) => {
    this.setState({
      commentText: event.target.value,
      commentValid: event.target.value.length <= 3000,
    })
  }

  public handleContactOffsetSecondsChange = (event: { target: { value: string } }) => {
    const { maxSecondsOffset, maxMinutesOffset, contactMinutesOffset } = this.state
    const seconds =
      Number(event.target.value) > maxSecondsOffset && contactMinutesOffset === maxMinutesOffset
        ? maxSecondsOffset
        : Number(event.target.value)

    this.setState({
      contactSecondsOffset: seconds,
    })
  }

  public handleContactOffsetMinutesChange = async (event: { target: { value: string } }) => {
    const { maxMinutesOffset, contactSecondsOffset } = this.state
    const minutes = Number(event.target.value) > maxMinutesOffset ? maxMinutesOffset : Number(event.target.value)

    await this.setState({
      contactMinutesOffset: minutes,
    })

    // trigger a change on the seconds input when the minutes are maxed out to handle not being over the seconds maximum
    if (minutes === maxMinutesOffset) {
      this.handleContactOffsetSecondsChange({
        target: {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: convert to string
          value: contactSecondsOffset as unknown as string,
        },
      })
    }
  }

  public renderCommentForm = () => {
    const { callDuration, leaveCommentMessage, testIdPrefix, classes, t } = this.props
    const {
      editComment,
      editingComment,
      commentText,
      commentValid,
      submittingComment,
      contactMinutesOffset,
      contactSecondsOffset,
      maxMinutesOffset,
      maxSecondsOffset,
    } = this.state

    // The chat message associated with the existing comment (if applicable). Only not null if we're editing an existing comment.
    const associatedChatBubble = this.props.indexedChatBubbles?.find(bubble => bubble.index === editingComment?.contactOffset) ?? null

    return (
      <form className={classes.commentForm}>
        <TextField
          label={leaveCommentMessage === null ? t(['leaveAComment', 'Leave a comment']) : leaveCommentMessage}
          placeholder={t(['addComment', 'Add Comment'])}
          multiline
          fullWidth
          maxRows={'40'}
          value={commentText}
          onChange={this.handleCommentChange}
          margin="dense"
          error={!commentValid}
          helperText={commentValid ? `${3000 - commentText.length} ${t('charactersRemaining')}` : t('maximumCharLimit')}
          InputProps={{
            classes: {
              root: classes.textFieldRoot,
            },
          }}
          inputProps={{
            'auto-qa-id': `${testIdPrefix}-comment-text-input`,
            'data-testid': `${testIdPrefix}-comment-text-input`,
          }}
        />
        { (callDuration > 0 || this.props.indexedChatBubbles?.length > 0) && (
          <div className={classes.timeOffset}>
            <InputLabel color="primary" focused>{t('location')}</InputLabel>
            <div className={classes.timeOffsetFields}>
              { (this.props.indexedChatBubbles?.length > 0) ? (
                <>
                  { associatedChatBubble ? (
                    <div>{associatedChatBubble.authorName} - {formatDuration(associatedChatBubble.relativeTimestampMs)}</div>
                  ) : (
                    <SelectedChatBubbleContext.Consumer>
                      { ctx => {
                        const selectedBubble = ctx.selectedBubbleId !== null ? this.props.indexedChatBubbles.find(bubble => bubble.index === ctx.selectedBubbleId) : null
                        return selectedBubble ? (
                          <div>{selectedBubble.authorName} - {formatDuration(selectedBubble.relativeTimestampMs)}</div>
                        ) : (
                          <div>{t('selectMessageToAddComment')}</div>
                        )
                      }}
                    </SelectedChatBubbleContext.Consumer>
                  )}
                </>
              ) : (
                <>
                  <TextField
                    id="minutes-offset"
                    label={t('minutes')}
                    value={contactMinutesOffset}
                    onChange={this.handleContactOffsetMinutesChange}
                    type="number"
                    inputProps={{
                      min: 0,
                      max: maxMinutesOffset,
                      'auto-qa-id': `${testIdPrefix}-comment-minutes-input`,
                      'data-testid': `${testIdPrefix}-comment-minutes-input`,
                    }}
                    disabled={editComment || maxMinutesOffset === 0}
                    className={classes.timeOffsetField}
                    InputProps={{
                      classes: {
                        input: classes.timeOffsetNumberInput,
                      },
                    }}
                  />
                  <TextField
                    id="seconds-offset"
                    label={t('seconds')}
                    value={contactSecondsOffset}
                    onChange={this.handleContactOffsetSecondsChange}
                    type="number"
                    inputProps={{
                      min: 0,
                      max: contactMinutesOffset === maxMinutesOffset ? maxSecondsOffset : 59,
                      'auto-qa-id': `${testIdPrefix}-comment-seconds-input`,
                      'data-testid': `${testIdPrefix}-comment-seconds-input`,
                    }}
                    disabled={editComment}
                    className={classes.timeOffsetField}
                    InputProps={{
                      classes: {
                        input: classes.timeOffsetNumberInput,
                      },
                    }}
                  />
                </>
              )}
            </div>
          </div>
        )}
        <div className={classes.commentFormFooter}>
          <Button
            size="small"
            onClick={this.hideForm}
            auto-qa-id={`${testIdPrefix}-comment-cancel-button`}
            data-testid={`${testIdPrefix}-comment-cancel-button`}
          >
            {t('cancel')}
          </Button>
          <SelectedChatBubbleContext.Consumer>
            { ctx => {
              const selectedBubble = ctx.selectedBubbleId ? this.props.indexedChatBubbles.find(bubble => bubble.index === ctx.selectedBubbleId) ?? null : null
              return (
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  onClick={editComment ? () => this.handleUpdateComment(selectedBubble) : () => this.handleAddComment(selectedBubble)}
                  disabled={
                    commentText.length > 3000
                    || commentText.length === 0
                    || submittingComment
                    || ((this.props.indexedChatBubbles?.length > 0) && ctx.selectedBubbleId === null && editingComment === null)
                  }
                  auto-qa-id={`${testIdPrefix}-comment-${editComment ? 'update' : 'add'}-button`}
                  data-testid={`${testIdPrefix}-comment-${editComment ? 'update' : 'add'}-button`}
                >
                  {editComment ? t(['updateComment', 'Update Comment']) : t(['addComment', 'Add Comment'])}
                </Button>
              )
            }}
          </SelectedChatBubbleContext.Consumer>
        </div>
      </form>
    )
  }

  public handleAddComment = (selectedBubble: IndexedChatBubble | null) => {
    const { sectionId, questionId, onCommentAdd } = this.props
    const { commentText, contactMinutesOffset, contactSecondsOffset } = this.state

    this.setState({
      submittingComment: true,
    })

    const contactOffset = (this.props.indexedChatBubbles?.length > 0)
      ? selectedBubble?.index ?? 0
      : contactMinutesOffset * 60000 + contactSecondsOffset * 1000

    onCommentAdd({
      text: commentText,
      contactOffset,
      // @ts-expect-error TODO: handle undefined case
      sectionFK: sectionId,
      // @ts-expect-error TODO: handle undefined case
      questionFK: questionId,
    })
  }

  public handleUpdateComment = (selectedBubble: IndexedChatBubble | null) => {
    const { sectionId, questionId, onCommentUpdate } = this.props
    const { commentText, editCommentRef, contactMinutesOffset, contactSecondsOffset } = this.state

    this.setState({
      submittingComment: true,
    })

    const contactOffset = (this.props.indexedChatBubbles?.length > 0)
      ? selectedBubble?.index ?? 0
      : contactMinutesOffset * 60000 + contactSecondsOffset * 1000

    onCommentUpdate(
      {
        text: commentText,
        contactOffset,
        // @ts-expect-error TODO: handle undefined case
        sectionFK: sectionId,
        // @ts-expect-error TODO: handle undefined case
        questionFK: questionId,
      },
      editCommentRef,
    )
  }

  public render() {
    const {
      comments,
      leaveCommentMessage,
      sectionId,
      testIdPrefix,
      disabled,
      classes,
      t,
      authenticatedUser,
      timezone,
      indexedChatBubbles,
    } = this.props
    const { showComments, showForm } = this.state
    const canComment = userCanComment(authenticatedUser)

    if (comments.length === 0 && !canComment) {
      return null
    }

    let label
    if (sectionId) {
      label = `eval-comments-${sectionId}`
    } else {
      label = 'eval-comments'
    }

    return (
      <div aria-label={label} className={classes.root}>
        <header
          className={classNames(classes.header, {
            [classes.isHidden]: showForm,
          })}
        >
          {comments.length > 0 && (
            <button
              className={classes.headerButton}
              onClick={this.toggleShowComments}
              data-testid={`${testIdPrefix}-comments-button`}
            >
              <BeamIcon className={classes.headerButtonIcon}>{showComments ? 'MenuDown' : 'MenuRight'}</BeamIcon>
              {` ${t('comments')} (${comments.length})`}
            </button>
          )}
          {canComment && comments.length > 0 && <span className={classes.pipeDivider}>|</span>}
          {canComment && (
            <ConditionalWrap
              condition={disabled}
              wrap={children => (
                <Tooltip
                  arrow
                  // @ts-expect-error TODO: upgrade to MUI v5
                  title={t('scoreAQuestionToEnableComments')}
                  PopperProps={{
                    disablePortal: true,
                  }}
                >
                  {children}
                </Tooltip>
              )}
            >
              <button
                className={classNames(classes.headerButton, {
                  [classes.headerButtonDisabled]: disabled,
                })}
                onClick={this.handleAddCommentClick}
                data-testid={`${testIdPrefix}-add-comment-button`}
              >
                {leaveCommentMessage === null ? t(['leaveAComment', 'Leave a comment']) : leaveCommentMessage}
              </button>
            </ConditionalWrap>
          )}
        </header>
        <Paper variant="outlined" square className={classNames(classes.comments, {
          [classes.showComments]: showComments && !showForm,
        })}>
          {comments.map(comment => {
            // @ts-expect-error TODO: fix this error
            const id = comment['$ref'].slice(comment['$ref'].lastIndexOf())

            return <Comment
              key={id}
              comment={comment}
              onEdit={this.handleCommentEdit}
              timezone={timezone}
              associatedChatBubble={indexedChatBubbles.length > 0 ? indexedChatBubbles.find(bubble => bubble.index === comment.contactOffset) ?? null : null}
            />
          })}
        </Paper>
        {showForm && this.renderCommentForm()}
      </div>
    )
  }
}

export default withStyles(styles)(withTranslation()(withAuthenticatedUser(EvaluationComments)))
